import { MutableRefObject, useCallback } from 'react';
import { useTheme } from '@mui/material';
import classNames from 'classnames/bind';
import { area, curveMonotoneX } from 'd3-shape';
import { ScaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { round } from 'lodash';

import { SWELL_SPECTRA_GRAPH_HEIGHT } from 'common/constants';

import { Swell } from 'types/swell';
import { BuoySpectraFormattedData } from 'types/buoys';
import styles from '../components/ForecastGraphSwellSpectraContainer.module.scss';

const cx = classNames.bind(styles);

interface SwellSpectraProps {
  graphXAxisTicks: Array<number> | null;
  buoySpectraTooltipYPosition: BuoySpectraFormattedData | null;
  isBuoySwellSpectraGraph?: boolean;
  canvasRef: MutableRefObject<HTMLDivElement | null>;
  energy: Array<number> | null;
  maxSwellEnergy: number;
  periodRange: Array<number> | null | undefined;
  numberOfTicks: number;
  sortedTop3Swells: Array<Swell>;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
}

const useSetupSwellSpectraGraph = ({
  graphXAxisTicks,
  buoySpectraTooltipYPosition,
  isBuoySwellSpectraGraph,
  canvasRef,
  energy,
  maxSwellEnergy,
  numberOfTicks,
  periodRange,
  sortedTop3Swells,
  xScale,
  yScale,
}: SwellSpectraProps) => {
  const theme = useTheme();
  const setupSwellSpectraGraph = useCallback(() => {
    if (!energy || !periodRange) {
      select(`.swell-spectra-graph-svg`).remove();
    }

    if (energy?.length && sortedTop3Swells.length && canvasRef?.current) {
      select(`.swell-spectra-graph-ticks`).remove();
      select(`.swell-spectra-graph-svg`).remove();
      select(`.swell-spectra-graph-x-axis-ticks`).remove();

      const graphData:
        | Array<{
            period: number;
            energy: number;
            tooltipYPosition: number | null | undefined;
          }>
        | undefined = periodRange?.map((period: number, index: number) => ({
        period,
        energy: energy?.[index],
        tooltipYPosition: isBuoySwellSpectraGraph
          ? buoySpectraTooltipYPosition?.[index]?.energy
          : energy?.[index],
      }));

      const graphContainerWidth = canvasRef.current.offsetWidth;

      // https://observablehq.com/@d3/area-chart
      const drawArea = area()
        .x((d: any) => xScale(d.period))
        .y0(yScale(0)) // Set the bottom y-coordinate to the y-coordinate of the bottom axis
        .y1((d: any) => yScale(d.energy)); // Set the top y-coordinate to the y-coordinate of the swellSpectraData point

      if (!isBuoySwellSpectraGraph) {
        drawArea.curve(curveMonotoneX);
      }

      const canvas = select(`.swell-spectra-graph-canvas`);

      if (numberOfTicks > 0) {
        const intermediateTicks = [...Array(numberOfTicks - 1)].map((_, index) =>
          round((index + 1) * (maxSwellEnergy / numberOfTicks), 2),
        );

        const tickData = [...intermediateTicks, round(maxSwellEnergy, 2)].reverse();

        const ticks = canvas.append('div').attr('class', cx('swell-spectra-graph-ticks'));

        ticks
          .selectAll('.swell-spectra-graph-tick')
          .data(tickData)
          .enter()
          .append('div')
          .attr('class', cx(['swell-spectra-graph-tick', styles.tick]))
          .style('top', (d, i) => `${i === 0 ? 10 : SWELL_SPECTRA_GRAPH_HEIGHT / (i + 1) - 10}px`)
          .text((d) => d);

        ticks
          .selectAll('.swell-spectra-graph-dashed-line')
          .data(tickData)
          .enter()
          .append('div')
          .attr('class', cx(['swell-spectra-graph-dashed-line', styles.dashedLine]))
          .style('top', (d, i) => `${i === 0 ? 15 : SWELL_SPECTRA_GRAPH_HEIGHT / (i + 1) - 5}px`);
      }

      const svg = canvas.append('svg').attr('class', cx(['swell-spectra-graph-svg', styles.svg]));
      const swellSpectraGroup = svg.append('g').attr('class', 'swell');

      const gradient = swellSpectraGroup
        .append('defs')
        .append('linearGradient')
        .attr('id', 'swellSpectraAreaGradient')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', yScale(0));

      // bottom of gradient
      gradient.append('stop').attr('offset', '0%').attr('stop-color', '#4D8BA7');
      // top of gradient
      gradient.append('stop').attr('offset', '100%').attr('stop-color', '#B9D3DF');

      swellSpectraGroup
        .append('path')
        .style('fill', 'url(#swellSpectraAreaGradient)')
        .attr('d', drawArea(graphData as any));

      if (sortedTop3Swells.length) {
        const swellCircles = svg.selectAll('.swell-circle').data(sortedTop3Swells);

        svg
          .selectAll('.swell-line')
          .data(sortedTop3Swells)
          .enter()
          .append('line')
          .attr('class', cx('swell-line'))
          .attr('x1', (d) => xScale(d.period) - 3.5 / 2)
          .attr('y1', SWELL_SPECTRA_GRAPH_HEIGHT - 10)
          .attr('x2', (d) => xScale(d.period) - 3.5 / 2)
          // This value may legitimately not be avaialble if the swell partition does not have a frequency (period) that aligns with our raw data.
          // We cast it as a number to satisfy typescript but prevent falling back to a default value that may cause phantom tooltips to appear.
          .attr('y2', (d) => yScale(graphData?.[d.period - 2]?.tooltipYPosition as number))
          .attr('display', (d) => (graphData?.[d.period - 2]?.tooltipYPosition ? 'block' : 'none'))
          .attr('stroke', theme.palette.text.secondary)
          .attr('stroke-dasharray', '3,3');

        swellCircles
          .enter()
          .append('circle')
          .attr('class', cx('swell-circle'))
          .attr('data-testid', 'swell-spectra-graph-swell-circle')
          .attr('cx', (d) => xScale(d.period) - 3.5 / 2)
          // This value may legitimately not be avaialble if the swell partition does not have a frequency (period) that aligns with our raw data.
          // We cast it as a number to satisfy typescript but prevent falling back to a default value that may cause phantom tooltips to appear.
          .attr('cy', (d) => yScale(graphData?.[d.period - 2]?.tooltipYPosition as number))
          .attr('display', (d) => (graphData?.[d.period - 2]?.tooltipYPosition ? 'block' : 'none'))
          .attr('r', 3.5)
          .attr('fill', '#171717')
          .attr('stroke', 'white')
          .attr('stroke-width', 1);
      }

      const svgTicks = canvas
        .append('svg')
        .attr('class', cx('swell-spectra-graph-x-axis-ticks', styles.xAxis));

      svgTicks
        .append('path')
        .attr('d', `M2 ${SWELL_SPECTRA_GRAPH_HEIGHT - 10}H${graphContainerWidth}`)
        .attr('stroke', theme.palette.text.secondary)
        .attr('stroke-width', '0.5');

      svgTicks
        .selectAll('path')
        .data(graphXAxisTicks || [])
        .enter()
        .append('path')
        .attr(
          'd',
          (period) =>
            `M${xScale(period)} ${SWELL_SPECTRA_GRAPH_HEIGHT - 10}V${
              SWELL_SPECTRA_GRAPH_HEIGHT - 7
            }`,
        )
        .attr('data-testid', 'swell-spectra-graph-period-ticks')
        .attr('stroke', theme.palette.text.secondary)
        .attr('stroke-width', '0.5');
    }
  }, [
    energy,
    sortedTop3Swells,
    canvasRef,
    periodRange,
    yScale,
    numberOfTicks,
    theme.palette.text.secondary,
    graphXAxisTicks,
    isBuoySwellSpectraGraph,
    buoySpectraTooltipYPosition,
    xScale,
    maxSwellEnergy,
  ]);
  return { setupSwellSpectraGraph };
};

export default useSetupSwellSpectraGraph;
